/** @jsx jsx */
import { css, jsx } from "@emotion/core"

/* eslint-disable prettier/prettier */

import React, { useEffect } from "react"
import { useGlobalState } from "./GlobalStateProvider"
import { SiteWrapper } from "./SiteWrapper"
import { renderRoutes, RouteConfig } from "react-router-config"
import { matchPath, Redirect, RouteComponentProps, useHistory, useLocation } from "react-router-dom"
import { useLastSeenUser } from "../hooks/useLastSeenUser"
import { eRoutes } from "../routes"
import { ModalProvider } from "./ModalProvider"
import { TextGroupLarge } from "@cd2/components"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useHrAreaOnly } from "../hooks/useHrAreaOnly"
import { CartProvider } from "./CartProvider"
import { LoadingScreen } from "./LoadingScreen"
import type { ICustomerShowRecord } from "../Pages/Customers/CustomerShowPage"
import { LoadingFrame } from "./LoadingFrame"
import { DeliveryNewPage } from "../Pages/DeliveryNewPage"
import { useApi } from "../hooks/useApi"
import { ManagedModalProvider } from "../hooks/ManagedModals"
import { LinkTooltipEditorHost } from "./LinkTooltips"
import { ThemeProvider } from "@cd2/theme"
import { themeColors } from "./EOSThemeProvider"
import { WebsiteProvider } from "./WebsiteProvider"
import { isChunkLoadError, reloadForNewBuild } from "../utils/chunkLoadError"
import { isExpiredSessionError, redirectForExpiredSession } from "../utils/expiredSession"

const AuditList = namedLazy(() => import("./AuditList"), "AuditList")
const CookiesBanner = namedLazy(() => import("./CookiesBanner"), "CookiesBanner")
const CustomerSiteWrapper = namedLazy(() => import("./CustomerSiteWrapper"), "CustomerSiteWrapper")
const MasterPriceImportsIndex = namedLazy(
  () => import("../Pages/MasterPriceImportsIndex"),
  "MasterPriceImportsIndex",
)

function namedLazy<Mod extends object, Key extends keyof Mod>(
  importer: () => Promise<Mod>,
  key: Key,
) {
  return React.lazy<React.ComponentType<Mod[Key] extends (props: infer U) => any ? U : never>>(() =>
    importer().then(mod => ({ default: mod[key] as any })),
  )
}

/* eslint-disable prettier/prettier */
const anonRoutes: RouteConfig[] = [
  {
    path: "/price-list",
    component: namedLazy(
      () => import("../Pages/AnonPriceListDownloadPage"),
      "AnonPriceListDownloadPage",
    ),
    exact: true,
  },
  {
    path: "/forgot-password",
    component: namedLazy(() => import("../Pages/ForgettenPasswordPage"), "ForgottenPasswordPage"),
    exact: true,
  },
  {
    path: "/reset-password",
    component: namedLazy(() => import("../Pages/UpdatePasswordPage"), "UpdatePasswordPage"),
    exact: true,
  },
  {
    path: "/accept-invite",
    component: namedLazy(() => import("../Pages/AcceptInvitePage"), "AcceptInvitePage"),
    exact: true,
  },
  {
    path: eRoutes["/terms-and-conditions"],
    component: namedLazy(
      () => import("../Pages/Sessions/TermsAndConditionsPage"),
      "TermsAndConditionsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/privacy-policy"],
    component: namedLazy(() => import("../Pages/Sessions/PrivacyPolicyPage"), "PrivacyPolicyPage"),
    exact: true,
  },
  {
    path: eRoutes["/support-and-returns"],
    component: namedLazy(
      () => import("../Pages/Sessions/SupportAndReturnsAnonPage"),
      "SupportAndReturnsAnonPage",
    ),
    exact: true,
  },
  {
    path: "/",
    component: namedLazy(() => import("../Pages/Sessions/LogInPage2"), "LogInPage2"),
    exact: true,
  },
  { component: () => <Redirect to="/" /> },
]

export const routes: RouteConfig[] = [
  { path: "/", component: namedLazy(() => import("../Pages/HomePage"), "HomePage"), exact: true },
  { path: "/2", component: () => <Redirect to="/" /> },
  {
    path: eRoutes["/customer-support/customer-page-banners"],
    exact: true,
    component: namedLazy(
      () => import("../Pages/CustomerPageBanners/CustomerPageBannersIndexPage"),
      "CustomerPageBannersIndexPage",
    ),
  },
  {
    path: eRoutes["/customer-support/customer-page-banners/new"],
    exact: true,
    component: namedLazy(
      () => import("../Pages/CustomerPageBanners/CustomerPageBannersNewPage"),
      "CustomerPageBannersNewPage",
    ),
  },
  {
    path: eRoutes["/customer-support/customer-page-banners/:id"],
    exact: true,
    component: namedLazy(
      () => import("../Pages/CustomerPageBanners/CustomerPageBannersEditPage"),
      "CustomerPageBannersEditPage",
    ),
  },
  {
    path: eRoutes["/system/sage-exports"],
    exact: true,
    component: namedLazy(
      () => import("../Pages/BranchSageExports/SageExportsDashboardList"),
      "SageExportsDashboardList",
    ),
  },
  {
    path: eRoutes["/system/sage-exports"],
    exact: true,
    component: namedLazy(
      () => import("../Pages/BranchSageExports/SageExportsDashboardList"),
      "SageExportsDashboardList",
    ),
  },
  {
    path: eRoutes["/system/sage-exports"],
    component: namedLazy(
      () => import("../Pages/BranchSageExports/SageExportsDashboard"),
      "SageExportsDashboard",
    ),
    routes: [
      {
        path: eRoutes["/system/sage-exports/customers"],
        component: namedLazy(
          () => import("../Pages/BranchSageExports/SageExportsCustomersIndex"),
          "SageExportsCustomersIndex",
        ),
        exact: true,
      },
      {
        path: eRoutes["/system/sage-exports/orders"],
        component: namedLazy(
          () => import("../Pages/BranchSageExports/SageExportsOrdersIndex"),
          "SageExportsOrdersIndex",
        ),
        exact: true,
      },
      {
        path: eRoutes["/system/sage-exports/price-imports"],
        component: namedLazy(
          () => import("../Pages/BranchSageExports/SageExportsPriceImportsIndex"),
          "SageExportsPriceImportsIndex",
        ),
        exact: true,
      },
      {
        path: eRoutes["/system/sage-exports/purchase-orders"],
        component: namedLazy(
          () => import("../Pages/BranchSageExports/SageExportsPurchaseOrdersIndex"),
          "SageExportsPurchaseOrdersIndex",
        ),
        exact: true,
      },
      {
        path: eRoutes["/system/sage-exports/stock-takes"],
        component: namedLazy(
          () => import("../Pages/BranchSageExports/SageExportsStockTakeExportsIndex"),
          "SageExportsStockTakeExportsIndex",
        ),
        exact: true,
      },
      {
        path: eRoutes["/system/sage-exports/stock-transfers"],
        component: namedLazy(
          () => import("../Pages/BranchSageExports/SageExportsStockTransfersIndex"),
          "SageExportsStockTransfersIndex",
        ),
        exact: true,
      },
      {
        path: eRoutes["/system/sage-exports/price-export-queue"],
        component: namedLazy(
          () => import("../Pages/BranchSageExports/SageExportsPriceExportQueueIndex"),
          "SageExportsPriceExportQueueIndex",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/addresses"],
    component: namedLazy(() => import("../Pages/AddressIndexPage"), "AddressIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/addresses/new"],
    component: namedLazy(() => import("../Pages/AddressNewPage"), "AddressNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/addresses/:id/preview"],
    component: namedLazy(() => import("../Pages/AddressShowPage"), "AddressShowPage"),
    exact: true,
  },
  {
    path: eRoutes["/deliveries"],
    component: namedLazy(() => import("../Pages/DeliveriesIndexPage"), "DeliveriesIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/deliveries/:id/fulfillment"],
    component: namedLazy(
      () => import("../Pages/DeliveryFulfillmentShowPage"),
      "DeliveryFulfillmentShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/deliveries/:id/new"],
    component: namedLazy(() => import("../Pages/DeliveryNewPage"), "DeliveryNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/deliveries/:id/preview"],
    component: namedLazy(() => import("../Pages/DeliveryShowPage"), "DeliveryShowPage"),
    exact: true,
  },
  {
    path: eRoutes["/drivers"],
    component: namedLazy(() => import("../Pages/DriversIndexPage"), "DriversIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/drivers/:id"],
    component: namedLazy(() => import("../Pages/DriversShowPage"), "DriversShowPage"),
    exact: true,
  },
  {
    path: eRoutes["/goods-receipts"],
    component: namedLazy(() => import("../Pages/GoodsReceiptsPage"), "GoodsReceiptsPage"),
    exact: true,
  },
  {
    path: eRoutes["/goods-receipts/all"],
    component: namedLazy(() => import("../Pages/GoodsReceiptsIndexPage"), "GoodsReceiptsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/goods-receipts/:id"],
    component: namedLazy(() => import("../Pages/GoodsReceiptShowPage"), "GoodsReceiptShowPage"),
    exact: true,
  },
  {
    path: eRoutes["/invoices"],
    component: namedLazy(() => import("../Pages/InvoicesIndexPage"), "InvoicesIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/invoices/:id/fulfillment"],
    component: namedLazy(
      () => import("../Pages/DeliveryFulfillmentShowPage"),
      "DeliveryFulfillmentShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/invoices/:id/new"],
    component: (props: any) => <DeliveryNewPage {...props} invoice />,
    exact: true,
  },
  {
    path: eRoutes["/invoices/:id/preview"],
    component: namedLazy(() => import("../Pages/DeliveryShowPage"), "DeliveryShowPage"),
    exact: true,
  },
  {
    path: eRoutes["/notifications"],
    component: namedLazy(() => import("../Pages/NotificationsPage"), "NotificationsPage"),
    exact: true,
  },
  {
    path: eRoutes["/order-requests"],
    component: namedLazy(
      () => import("../Pages/OrderRequests/OrderRequestsIndexPage"),
      "OrderRequestsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/order-requests/new"],
    component: namedLazy(
      () => import("../Pages/OrderRequests/OrderRequestsNewPage"),
      "OrderRequestsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/order-requests/:id/edit"],
    component: namedLazy(
      () => import("../Pages/OrderRequests/OrderRequestsEditPage"),
      "OrderRequestsEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/order-requests/:id/log"],
    component: namedLazy(
      () => import("../Pages/OrderRequests/OrderRequestsShowHistoryPage"),
      "OrderRequestsShowHistoryPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/order-requests/:id/preview"],
    component: namedLazy(
      () => import("../Pages/OrderRequests/OrderRequestsShowPage"),
      "OrderRequestsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/order-requests/incoming"],
    component: namedLazy(
      () => import("../Pages/OrderRequests/OrderRequestsIndexPage"),
      "OrderRequestsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/order-requests/outgoing"],
    component: namedLazy(
      () => import("../Pages/OrderRequests/OrderRequestsIndexPage"),
      "OrderRequestsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/orders"],
    component: namedLazy(() => import("../Pages/OrdersIndexPage"), "OrdersIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/orders/new"],
    component: namedLazy(() => import("../stories/orders/OrdersNewPage"), "OrdersNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/orders/:id/edit"],
    component: namedLazy(() => import("../stories/orders/OrdersEditPage"), "OrdersEditPage"),
    exact: true,
  },
  {
    path: eRoutes["/orders/:id/log"],
    component: namedLazy(() => import("../Pages/OrderShowHistoryPage"), "OrderShowHistoryPage"),
    exact: true,
  },
  {
    path: eRoutes["/orders/:id/preview"],
    component: namedLazy(() => import("../Pages/OrderShowPage"), "OrderShowPage"),
    exact: true,
  },
  {
    path: eRoutes["/orders/:id/development"],
    component: namedLazy(
      () => import("../Pages/OrderShowDevelopmentPage"),
      "OrderShowDevelopmentPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/purchase-orders"],
    component: namedLazy(
      () => import("../Pages/PurchaseOrdersIndexRoute"),
      "PurchaseOrdersIndexRoute",
    ),
    exact: true,
  },
  {
    path: eRoutes["/purchase-orders/new"],
    component: namedLazy(() => import("../Pages/PurchaseOrdersNewRoute"), "PurchaseOrdersNewRoute"),
    exact: true,
  },
  {
    path: eRoutes["/purchase-orders/:id"],
    component: namedLazy(
      () => import("../Pages/PurchaseOrdersShowRoute"),
      "PurchaseOrdersShowRoute",
    ),
    exact: true,
  },
  {
    path: eRoutes["/purchase-orders/:id/edit"],
    component: namedLazy(
      () => import("../Pages/PurchaseOrdersEditRoute"),
      "PurchaseOrdersEditRoute",
    ),
    exact: true,
  },
  {
    path: eRoutes["/purchase-orders/:id/log"],
    component: namedLazy(() => import("../Pages/PurchaseOrdersLogRoute"), "PurchaseOrdersLogRoute"),
    exact: true,
  },
  {
    path: eRoutes["/purchase-orders/group"],
    component: namedLazy(() => import("../Pages/PurchaseOrderGroupPage"), "PurchaseOrderGroupPage"),
    exact: true,
  },
  {
    path: eRoutes["/quotes"],
    component: namedLazy(() => import("../Pages/QuotesIndexPage"), "QuotesIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/quotes/new"],
    component: namedLazy(() => import("../stories/quotes/QuotesNewPage"), "QuotesNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/quotes/:id/edit"],
    component: namedLazy(() => import("../stories/quotes/QuotesEditPage"), "QuotesEditPage"),
    exact: true,
  },
  {
    path: eRoutes["/quotes/:id/log"],
    component: namedLazy(() => import("../Pages/QuoteShowHistoryPage"), "QuoteShowHistoryPage"),
    exact: true,
  },
  {
    path: eRoutes["/quotes/:id/preview"],
    component: namedLazy(() => import("../Pages/QuoteShowPage"), "QuoteShowPage"),
    exact: true,
  },
  {
    component: namedLazy(
      () => import("../Pages/SubCustomers/SubCustomerShowPage"),
      "SubCustomerShowPage",
    ),
    path: eRoutes["/sub-customers/:id"],
    routes: [
      {
        path: eRoutes["/sub-customers/:id"],
        component: namedLazy(
          () => import("../Pages/SubCustomers/SubCustomerShowSummaryPage"),
          "SubCustomerShowSummaryPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/sub-customers/:id/contacts"],
        component: namedLazy(
          () => import("../Pages/SubCustomers/SubCustomerShowContactsPage"),
          "SubCustomerShowContactsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/sub-customers/:id/quotes"],
        component: namedLazy(
          () => import("../Pages/SubCustomers/SubCustomerShowQuotesPage"),
          "SubCustomerShowQuotesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/sub-customers/:id/orders"],
        component: namedLazy(
          () => import("../Pages/SubCustomers/SubCustomerShowOrdersPage"),
          "SubCustomerShowOrdersPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/sub-customers/:id/history"],
        component: namedLazy(
          () => import("../Pages/SubCustomers/SubCustomerShowHistoryPage"),
          "SubCustomerShowHistoryPage",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/customers"],
    component: namedLazy(
      () => import("../Pages/Customers/CustomersIndexPage"),
      "CustomersIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customers/merge"],
    component: namedLazy(
      () => import("../Pages/Customers/CustomersMergePage"),
      "CustomersMergePage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customers/new"],
    component: namedLazy(() => import("../Pages/Customers/CustomersNewPage"), "CustomersNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/customers/:id/edit"],
    component: namedLazy(
      () => import("../Pages/Customers/CustomerShowEditPage"),
      "CustomerShowEditPage",
    ),
    exact: true,
  },
  {
    component: namedLazy(() => import("../Pages/Customers/CustomerShowPage"), "CustomerShowPage"),
    path: eRoutes["/customers/:id"],
    routes: [
      {
        path: eRoutes["/customers/:id"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowSummaryPage"),
          "CustomerShowSummaryPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/audit"],
        component: ({ customer }: { customer: ICustomerShowRecord }) => (
          <AuditList id={customer.id} />
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/sub-customers"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowSubCustomersPage"),
          "CustomerShowSubCustomersPage",
        ),
        exact: true,
      },

      {
        path: eRoutes["/customers/:id/statements"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomersShowStatementsPage"),
          "CustomersShowStatementsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/nearby"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomersNearbyCustomers"),
          "CustomersNearbyCustomers",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/product-group-discounts"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerProductGroupDiscountsPage"),
          "CustomerProductGroupDiscountsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/communications"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerCommunicationsPage"),
          "CustomerCommunicationsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/events"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerMicrosoftEventsPage"),
          "CustomerMicrosoftEventsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/product-group-rebates"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerProductGroupRebatesPage"),
          "CustomerProductGroupRebatesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/support"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowSupportTickets"),
          "CustomerShowSupportTickets",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/contacts"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowContactsPage"),
          "CustomerShowContactsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/quotes"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowQuotesPage"),
          "CustomerShowQuotesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/rmas"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowReturnRequestsPage"),
          "CustomerShowReturnRequestsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/orders"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowOrdersPage"),
          "CustomerShowOrdersPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/deliveries"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowDeliveriesPage"),
          "CustomerShowDeliveriesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/addresses"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowAddressesPage"),
          "CustomerShowAddressesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/price-lists"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowPriceListsPage"),
          "CustomerShowPriceListsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/price-bands"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowPriceBandsPage"),
          "CustomerShowPriceBandsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/price-bands/named"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerNamedPriceBandEditPage"),
          "CustomerNamedPriceBandEditPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/history"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowHistoryPage"),
          "CustomerShowHistoryPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/price-overrides"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerPriceOverridesPage"),
          "CustomerPriceOverridesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/price-enquiry"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerPriceEnquiryPage"),
          "CustomerPriceEnquiryPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/products"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowProductsIndexPage"),
          "CustomerShowProductsIndexPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/restricted-product-groups"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerRestrictedProductGroupsPage"),
          "CustomerRestrictedProductGroupsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/projects"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomersProjectsPage"),
          "CustomersProjectsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/universal-price-bands"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerUniversalPriceBandsPage"),
          "CustomerUniversalPriceBandsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/tags"],
        component: namedLazy(() => import("../Pages/Customers/CustomerTags"), "CustomerTags"),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/memos"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerMemosPage"),
          "CustomerMemosPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/payments"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerPaymentsPage"),
          "CustomerPaymentsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customers/:id/purchased-products"],
        component: namedLazy(
          () => import("../Pages/Customers/CustomerShowPurchasedProductsPage"),
          "CustomerShowPurchasedProductsPage",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/warehouse/pending-picks"],
    component: namedLazy(
      () => import("../Pages/WarehouseSystem/PendingPicksIndex"),
      "PendingPicksIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/warehouse/pending-picks/:id"],
    component: namedLazy(
      () => import("../Pages/WarehouseSystem/PendingPicksShow"),
      "PendingPicksShow",
    ),
    exact: true,
  },
  {
    path: eRoutes["/quote-requests"],
    component: namedLazy(
      () => import("../Pages/JobsInternal/JobsInternalIndexPage"),
      "JobsInternalIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/quote-requests/new"],
    component: namedLazy(
      () => import("../Pages/JobsInternal/JobsInternalNewPage"),
      "JobsInternalNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/quote-requests/:id/preview"],
    component: namedLazy(() => import("../Pages/JobsInternal/JobsShowPage"), "JobShowPageLoader"),
    exact: true,
  },
  {
    path: eRoutes["/quote-requests/:id/edit"],
    component: namedLazy(
      () => import("../Pages/JobsInternal/JobsInternalEditPage"),
      "JobsInternalEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/quote-requests/:id/modify"],
    component: namedLazy(() => import("../Pages/Jobs/JobsEditPage"), "JobsEditPage"),
    exact: true,
  },
  {
    path: eRoutes["/jobs"],
    component: namedLazy(() => import("../Pages/Jobs/JobsIndexPage"), "JobsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/jobs/:id/preview"],
    component: namedLazy(() => import("../Pages/JobsInternal/JobsShowPage"), "JobShowPageLoader"),
    exact: true,
  },
  {
    path: eRoutes["/jobs/:id/edit"],
    component: namedLazy(() => import("../Pages/Jobs/JobsEditPage"), "JobsEditPage"),
    exact: true,
  },
  { path: eRoutes["/price-imports"], component: () => <MasterPriceImportsIndex forBranch /> },
  {
    path: eRoutes["/price-imports/new"],
    component: namedLazy(
      () => import("../Pages/SupplierPriceImports/SupplierPriceImportsNewPage"),
      "SupplierPriceImportsNewPage",
    ),
    exact: true,
  },
  { path: eRoutes["/stock/all-price-imports"], component: () => <MasterPriceImportsIndex /> },
  {
    path: eRoutes["/stock/bom-items"],
    component: namedLazy(
      () => import("../Pages/BuildSets/BuildSetsIndexPage"),
      "BuildSetsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/bom-items/new"],
    component: namedLazy(() => import("../Pages/BuildSets/BuildSetsNewPage"), "BuildSetsNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/stock/bom-items/:id"],
    component: namedLazy(() => import("../Pages/BuildSets/BuildSetsEditPage"), "BuildSetsEditPage"),
  },
  {
    path: eRoutes["/suppliers"],
    component: namedLazy(() => import("../Pages/SuppliersIndexPage"), "SuppliersIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/suppliers/:supplierId/price-imports/:id"],
    component: namedLazy(
      () => import("../Pages/SupplierPriceImports/SupplierPriceImportsEditPage"),
      "SupplierPriceImportsEditPage",
    ),
  },
  {
    path: eRoutes["/suppliers/bulk/price-imports"],
    component: namedLazy(
      () => import("../Pages/SupplierBulkPriceImports/SupplierBulkPriceImportsNewPage"),
      "SupplierBulkPriceImportsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/suppliers/:id/import-prices"],
    component: namedLazy(
      () => import("../Pages/SupplierPriceImports/SupplierPriceImportsNewPage"),
      "SupplierPriceImportsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/suppliers/:id"],
    component: namedLazy(() => import("../Pages/SuppliersShowPage"), "SuppliersShowPage"),
    routes: [
      {
        path: eRoutes["/suppliers/:id/price-imports"],
        component: namedLazy(
          () => import("../Pages/SuppliersShowMasterPriceImportsIndexPage"),
          "SuppliersShowMasterPriceImportsIndexPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/suppliers/:id/addresses"],
        component: namedLazy(
          () => import("../Pages/SupplierShowAddressesPage"),
          "SupplierShowAddressesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/suppliers/:id/edit"],
        component: namedLazy(() => import("../Pages/SuppliersEditPage"), "SuppliersEditPage"),
        exact: true,
      },
      {
        path: eRoutes["/suppliers/:id"],
        component: namedLazy(
          () => import("../Pages/SuppliersShowProductsIndexPage"),
          "SuppliersShowProductsIndexPage",
        ),
      },
    ],
  },
  {
    path: eRoutes["/stock/price-bands"],
    component: namedLazy(
      () => import("../Pages/Stock/PriceBands/PriceBandsIndexPage"),
      "PriceBandsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/price-bands/new"],
    component: namedLazy(
      () => import("../Pages/Stock/PriceBands/PriceBandsNewPage"),
      "PriceBandsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/buying-groups"],
    component: namedLazy(
      () => import("../Pages/BuyingGroups/BuyingGroupsIndex"),
      "BuyingGroupsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/buying-groups/new"],
    component: namedLazy(
      () => import("../Pages/BuyingGroups/BuyingGroupsNewPage"),
      "BuyingGroupsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/buying-groups/:id"],
    component: namedLazy(
      () => import("../Pages/BuyingGroups/BuyingGroupShowPage"),
      "BuyingGroupShowPage",
    ),
    routes: [
      {
        path: eRoutes["/stock/buying-groups/:id"],
        component: namedLazy(
          () => import("../Pages/BuyingGroups/BuyingGroupEditPage"),
          "BuyingGroupEditPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/buying-groups/:id/customers"],
        component: namedLazy(
          () => import("../Pages/BuyingGroups/BuyingGroupShowCustomers"),
          "BuyingGroupShowCustomers",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/buying-groups/:id/rebates"],
        component: namedLazy(
          () => import("../Pages/BuyingGroups/BuyingGroupShowRebates"),
          "BuyingGroupShowRebates",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/buying-groups/:id/price-bands"],
        component: namedLazy(
          () => import("../Pages/BuyingGroups/BuyingGroupShowPriceBands"),
          "BuyingGroupShowPriceBands",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/stock/price-bands/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/PriceBands/PriceBandShowPage"),
      "PriceBandShowPage",
    ),
    routes: [
      {
        path: eRoutes["/stock/price-bands/:id"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/Stock/PriceBands/PriceBandEditPage"),
          "PriceBandEditPage",
        ),
      },
      {
        path: eRoutes["/stock/price-bands/:id/customers"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/Stock/PriceBands/PriceBandsCustomersPage"),
          "PriceBandsCustomersPage",
        ),
      },
      {
        path: eRoutes["/stock/price-bands/:id/edit"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/Stock/PriceBands/PriceBandEditDetailsPage"),
          "PriceBandEditDetailsPage",
        ),
      },
      {
        path: eRoutes["/stock/price-bands/:id/log"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/Stock/PriceBands/PriceBandLogPage"),
          "PriceBandLogPage",
        ),
      },
      {
        path: eRoutes["/stock/price-bands/:id/audit"],
        component: ({ priceBand }: { priceBand: { id: string } }) => (
          <AuditList id={priceBand.id} />
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/price-bands/:id/imports"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/Stock/PriceBands/PriceBandsShowImportsPage"),
          "PriceBandsShowImportsPage",
        ),
      },
      {
        path: eRoutes["/stock/price-bands/:id/imports/new"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/Stock/PriceBands/PriceBandsImportPage"),
          "PriceBandsImportPage",
        ),
      },
    ],
  },
  {
    path: eRoutes["/stock/shared-price-bands"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/SharedPriceBandsIndexPage"),
      "SharedPriceBandsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/shared-price-bands/new"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsNewPage"),
      "UniversalPriceBandsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/shared-price-bands/import/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsImportPage"),
      "UniversalPriceBandsImportPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/shared-price-bands/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowPage"),
      "UniversalPriceBandsShowPage",
    ),
    routes: [
      {
        path: eRoutes["/stock/shared-price-bands/:id"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowProductsPage"),
          "UniversalPriceBandsShowProductsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/shared-price-bands/:id/customers"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowCustomers"),
          "UniversalPriceBandsShowCustomers",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/shared-price-bands/:id/imports"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowImportsPage"),
          "UniversalPriceBandsShowImportsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/shared-price-bands/:id/edit"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandEditPage"),
          "UniversalPriceBandEditPage",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/stock/universal-price-bands"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsIndexPage"),
      "UniversalPriceBandsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/universal-price-bands/new"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsNewPage"),
      "UniversalPriceBandsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/universal-price-bands/import/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsImportPage"),
      "UniversalPriceBandsImportPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/universal-price-bands/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowPage"),
      "UniversalPriceBandsShowPage",
    ),
    routes: [
      {
        path: eRoutes["/stock/universal-price-bands/:id"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowProductsPage"),
          "UniversalPriceBandsShowProductsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/universal-price-bands/:id/customers"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowCustomers"),
          "UniversalPriceBandsShowCustomers",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/universal-price-bands/:id/imports"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandsShowImportsPage"),
          "UniversalPriceBandsShowImportsPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/universal-price-bands/:id/edit"],
        component: namedLazy(
          () => import("../Pages/Stock/UniversalPriceBands/UniversalPriceBandEditPage"),
          "UniversalPriceBandEditPage",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/stock/product-master-importer"],
    component: namedLazy(() => import("../Pages/ProductMasterImporter"), "ProductMasterImporter"),
    exact: true,
  },
  {
    path: eRoutes["/stock/lines-of-business"],
    component: namedLazy(
      () => import("../Pages/Stock/LineOfBusiness/LineOfBusinessIndexPage"),
      "LineOfBusinessIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/lines-of-business/new"],
    component: namedLazy(
      () => import("../Pages/Stock/LineOfBusiness/LineOfBusinessNewPage"),
      "LineOfBusinessNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/lines-of-business/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/LineOfBusiness/LineOfBusinessEditPage"),
      "LineOfBusinessEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/supplier-masters"],
    component: namedLazy(
      () => import("../Pages/SupplierMastersIndexPage"),
      "SupplierMastersIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/supplier-masters/new"],
    component: namedLazy(() => import("../Pages/SupplierMastersNewPage"), "SupplierMastersNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/stock/supplier-masters/:id"],
    component: namedLazy(
      () => import("../Pages/SupplierMastersEditPage"),
      "SupplierMastersEditPage",
    ),
  },
  {
    path: eRoutes["/price-imports/:id"],
    component: namedLazy(
      () => import("../Pages/SupplierPriceImports/SupplierPriceImportsShowPage"),
      "SupplierPriceImportsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock"],
    component: namedLazy(() => import("../Pages/Stock/StockDashboardPage"), "StockDashboardPage"),
    exact: true,
  },
  {
    path: eRoutes["/internal-tickets"],
    component: namedLazy(() => import("./InternalTicketsPage"), "InternalTicketsPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/feedback"],
    component: namedLazy(() => import("./InternalTicketsIndexPage"), "InternalTicketsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/finance/invoice-adjustments"],
    component: namedLazy(
      () => import("../Pages/ManualInvoiceAdjustments/ManualInvoiceAdjustmentsIndex"),
      "ManualInvoiceAdjustmentsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/finance/invoice-adjustments/new"],
    component: namedLazy(
      () => import("../Pages/ManualInvoiceAdjustments/ManualInvoiceAdjustmentsNewPage"),
      "ManualInvoiceAdjustmentsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/finance/invoice-adjustments/:id"],
    component: namedLazy(
      () => import("../Pages/ManualInvoiceAdjustments/ManualInvoiceAdjustmentsEditPage"),
      "ManualInvoiceAdjustmentsEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/orders/mass-export"],
    component: namedLazy(() => import("./Pages/BulkExportOrdersPage"), "BulkExportOrdersPage"),
    exact: true,
  },
  {
    path: eRoutes["/stock/products/consignment"],
    component: namedLazy(
      () => import("../Pages/Stock/Consignment/ProductsConsignmentPage"),
      "ProductsConsignmentPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/manufacturers"],
    component: namedLazy(
      () => import("../Pages/Stock/Manufacturers/ManufacturersIndexPage"),
      "ManufacturersIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/serial-numbers"],
    component: namedLazy(
      () => import("../Pages/Stock/SerialNumbers/SerialNumbersIndexPage"),
      "SerialNumbersIndexPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/brands"],
    component: namedLazy(() => import("../Pages/Stock/Brands/BrandsIndexPage"), "BrandsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/stock/brands/new"],
    component: namedLazy(() => import("../Pages/Stock/Brands/BrandsNewPage"), "BrandsNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/stock/brands/:id"],
    component: namedLazy(() => import("../Pages/Stock/Brands/BrandsEditPage"), "BrandsEditPage"),
    exact: true,
  },

  {
    path: eRoutes["/stock/slim-stock"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockSyncPage"),
      "SlimStockSyncPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/purchase-orders"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockPurchaseOrdersPage"),
      "SlimStockPurchaseOrdersPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/article-code-master"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockArticleCodeMasterPage"),
      "SlimStockArticleCodeMasterPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/article-filter"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockArticleFilterPage"),
      "SlimStockArticleFilterPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/confirmed-demand"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockConfirmedDemandPage"),
      "SlimStockConfirmedDemandPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/historical-purchase-orders"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockHistoricalPurchaseOrdersPage"),
      "SlimStockHistoricalPurchaseOrdersPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/logistics"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockLogisticsPage"),
      "SlimStockLogisticsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/stock"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockStockPage"),
      "SlimStockStockPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/slim-stock/import-purchase-orders"],
    component: namedLazy(
      () => import("../Pages/Stock/SlimStock/SlimStockImportPurchaseOrders"),
      "SlimStockImportPurchaseOrders",
    ),
    exact: true,
  },

  {
    path: eRoutes["/crm/tasks"],
    component: namedLazy(() => import("../Pages/TasksPage"), "TasksPage"),
    exact: true,
  },
  {
    path: eRoutes["/crm/customer-lists"],
    component: namedLazy(() => import("../Pages/CRM/CRMCustomerLists"), "CRMCustomerLists"),
    exact: true,
  },
  {
    path: eRoutes["/crm/tags"],
    component: namedLazy(() => import("../Pages/Tags/TagsIndexPage"), "TagsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/crm/tags/new"],
    component: namedLazy(() => import("../Pages/Tags/TagsNewPage"), "TagsNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/crm/tags/:id"],
    component: namedLazy(() => import("../Pages/Tags/TagsEditPage"), "TagsEditPage"),
    exact: true,
  },

  {
    path: eRoutes["/customer-support"],
    component: namedLazy(
      () => import("../Pages/CustomerSupportDashboardPage"),
      "CustomerSupportDashboardPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/groups"],
    component: namedLazy(
      () => import("../Pages/CustomerSupportGroupsPage"),
      "CustomerSupportGroupsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/support-tickets"],
    component: namedLazy(
      () => import("../Pages/SupportTicketsIndexPage"),
      "SupportTicketsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/support-tickets/new"],
    component: namedLazy(() => import("../Pages/SupportTicketsNewPage"), "SupportTicketsNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/support-tickets/:id"],
    component: namedLazy(() => import("../Pages/SupportTicketsShowPage"), "SupportTicketsShowPage"),
    routes: [
      {
        path: eRoutes["/customer-support/support-tickets/:id"],
        component: namedLazy(
          () => import("../Pages/SupportTicketsEditPage"),
          "SupportTicketsEditPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/customer-support/support-tickets/:id/history"],
        component: namedLazy(
          () => import("../Pages/SupportTicketsHistoryPage"),
          "SupportTicketsHistoryPage",
        ),
      },
    ],
  },
  {
    path: eRoutes["/customer-support/settings"],
    component: namedLazy(
      () => import("../Pages/CustomerSupportSettingsPage"),
      "CustomerSupportSettingsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/stats"],
    component: namedLazy(
      () => import("../Pages/CustomerSupportStatsPage"),
      "CustomerSupportStatsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/spending"],
    component: namedLazy(
      () => import("../Pages/CustomerSupportSpending/CustomerSupportSpendingPage"),
      "CustomerSupportSpendingPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/products"],
    component: namedLazy(
      () => import("../Pages/Stock/Products/ProductsIndexPage"),
      "ProductsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/products/new"],
    component: namedLazy(
      () => import("../Pages/Stock/Products/ProductsNewPage"),
      "ProductsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/minimum-price-imports"],
    component: namedLazy(
      () => import("../Pages/Stock/MinimumPriceImports/MinimumPriceImportsIndexPage"),
      "MinimumPriceImportsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/minimum-price-imports/new"],
    component: namedLazy(
      () => import("../Pages/Stock/MinimumPriceImports/MinimumPriceImportsImportPage"),
      "MinimumPriceImportsImportPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/minimum-price-imports/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/MinimumPriceImports/MinimumPriceImportsShowPage"),
      "MinimumPriceImportsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/products/:id/edit"],
    component: namedLazy(
      () => import("../Pages/Stock/Products/ProductsEditPage"),
      "ProductsEditPage",
    ),
    exact: true,
  },
  {
    component: namedLazy(() => import("../Pages/Stock/Products/ProductsShowPage"), "ProductsShow"),
    path: eRoutes["/stock/products/:id"],
    routes: [
      {
        path: eRoutes["/stock/products/:id/orders"],
        component: namedLazy(
          () => import("../Pages/ProductShowOrdersPage"),
          "ProductShowOrdersPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/products/:id/quotes"],
        component: namedLazy(
          () => import("../Pages/ProductShowQuotesPage"),
          "ProductShowQuotesPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/products/:id/quote-email"],
        component: namedLazy(
          () => import("../Pages/Stock/Products/ProductsShowQuoteEmail"),
          "ProductsShowQuoteEmail",
        ),
      },
      {
        path: eRoutes["/stock/products/:id/locations"],
        component: namedLazy(
          () => import("../Pages/Stock/Products/ProductLocationsPage"),
          "ProductLocationsPage",
        ),
      },
      {
        path: eRoutes["/stock/products/:id/merge"],
        component: namedLazy(
          () => import("../Pages/Stock/Products/ProductMergePage"),
          "ProductMergePage",
        ),
      },
      {
        path: eRoutes["/stock/products/:id"],
        component: namedLazy(
          () => import("../Pages/Stock/Products/ProductsShowPage"),
          "ProductsShowPage",
        ),
      },
    ],
  },

  {
    path: eRoutes["/stock/categories"],
    component: namedLazy(
      () => import("../Pages/Stock/Categories/CategoriesIndexPage"),
      "CategoriesIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/categories/new"],
    component: namedLazy(
      () => import("../Pages/Stock/Categories/CategoriesNewPage"),
      "CategoriesNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/categories/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/Categories/CategoriesEditPage"),
      "CategoriesEditPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/product-groups"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductGroups/ProductGroupsIndex"),
      "ProductGroupsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/product-groups/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductGroups/ProductGroupsShowPage"),
      "ProductGroupsShowPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/warehouses"],
    component: namedLazy(
      () => import("../Pages/Stock/Warehouses/WarehousesIndex"),
      "WarehousesIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/warehouses/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/Warehouses/WarehousesShowPage"),
      "WarehousesShowPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/product-master-groups"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductMasterGroups/ProductMasterGroupsIndex"),
      "ProductMasterGroupsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/product-master-groups/new"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductMasterGroups/ProductMasterGroupsNewPage"),
      "ProductMasterGroupsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/product-master-groups/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductMasterGroups/ProductMasterGroupsShowPage"),
      "ProductMasterGroupsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/product-master-groups/:id/edit"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductMasterGroups/ProductMasterGroupsEditPage"),
      "ProductMasterGroupsEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/product-group-masters"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductGroupMasters/ProductGroupMastersIndex"),
      "ProductGroupMastersIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/product-masters"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductMasters/ProductMastersIndex"),
      "ProductMastersIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/categories"],
    component: namedLazy(
      () => import("../Pages/Stock/Categories/CategoriesIndexPage"),
      "CategoriesIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/categories/new"],
    component: namedLazy(
      () => import("../Pages/Stock/Categories/CategoriesNewPage"),
      "CategoriesNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/categories/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/Categories/CategoriesEditPage"),
      "CategoriesEditPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/product-groups"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductGroups/ProductGroupsIndex"),
      "ProductGroupsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/product-groups/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductGroups/ProductGroupsShowPage"),
      "ProductGroupsShowPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/warehouses"],
    component: namedLazy(
      () => import("../Pages/Stock/Warehouses/WarehousesIndex"),
      "WarehousesIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/warehouses/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/Warehouses/WarehousesShowPage"),
      "WarehousesShowPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/stock/product-group-masters"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductGroupMasters/ProductGroupMastersIndex"),
      "ProductGroupMastersIndex",
    ),
    exact: true,
  },
  {
    component: namedLazy(
      () => import("../Pages/Stock/ProductGroupMasters/ProductGroupMastersShowPage"),
      "ProductGroupMastersShowPage",
    ),
    path: eRoutes["/stock/product-group-masters/:id"],
    routes: [
      {
        path: eRoutes["/stock/product-group-masters/:id/sales-restrictions"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductGroupMasters/ProductGroupMastersSalesRestrictions"),
          "ProductGroupMastersSalesRestrictions",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/product-group-masters/:id"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductGroupMasters/ProductGroupMastersEditPage"),
          "ProductGroupMastersEditPage",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/stock/product-masters"],
    component: namedLazy(
      () => import("../Pages/Stock/ProductMasters/ProductMastersIndex"),
      "ProductMastersIndex",
    ),
    exact: true,
  },
  {
    component: namedLazy(
      () => import("../Pages/Stock/ProductMasters/ProductMastersShowPage"),
      "ProductMastersShowPage",
    ),
    path: eRoutes["/stock/product-masters/:id"],
    routes: [
      {
        path: eRoutes["/stock/product-masters/:id"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductMasters/ProductMastersShow"),
          "ProductMastersShow",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/product-masters/:id/edit"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductMasters/ProductMastersEdit"),
          "ProductMastersEdit",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/product-masters/:id/audit"],
        component: ({ productMaster }: { productMaster: { id: string } }) => (
          <AuditList id={productMaster.id} />
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/product-masters/:id/files"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductMasters/ProductMastersFiles"),
          "ProductMastersFiles",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/product-masters/:id/images"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductMasters/ProductMastersImages"),
          "ProductMastersImages",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/product-masters/:id/products"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductMasters/ProductMastersProductDetail"),
          "ProductMastersProductDetail",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/product-masters/:id/tags"],
        component: namedLazy(
          () => import("../Pages/Stock/ProductMasters/ProductMasterTags"),
          "ProductMasterTags",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/stock/universal-price-lists"],
    component: namedLazy(
      () => import("../Pages/UniversalPriceLists/UniversalPriceListsIndex"),
      "UniversalPriceListsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-transfers"],
    component: namedLazy(
      () => import("../Pages/Stock/WarehouseTransfers/StockTransfersIndexPage"),
      "StockTransfersIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-transfers/new"],
    component: namedLazy(
      () => import("../Pages/Stock/WarehouseTransfers/StockTransfersNewPage"),
      "StockTransfersNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-transfers/:id/edit"],
    component: namedLazy(
      () => import("../Pages/Stock/WarehouseTransfers/StockTransfersEditPage"),
      "StockTransfersEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-transfers/:id"],
    component: namedLazy(
      () => import("../Pages/Stock/WarehouseTransfers/StockTransfersShowPage"),
      "StockTransfersShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-takes"],
    component: namedLazy(
      () => import("../Pages/Stock/StockTakes/StockTakesIndex"),
      "StockTakesIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-takes/export-errors"],
    component: namedLazy(
      () => import("../Pages/Stock/StockTakes/StockTakeExportErrorsPage"),
      "StockTakeExportErrorsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-takes/new"],
    component: namedLazy(() => import("../Pages/Stock/StockTakes/StockTakesNew"), "StockTakesNew"),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-takes/import"],
    component: namedLazy(
      () => import("../Pages/Stock/StockTakes/StockTakeImportPage"),
      "StockTakeImportPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/stock-takes/:id/counts/:countId"],
    component: namedLazy(
      () => import("../Pages/Stock/StockTakes/StockTakesShowCountsShow"),
      "StockTakesShowCountsShow",
    ),
    exact: true,
  },
  {
    component: namedLazy(
      () => import("../Pages/Stock/StockTakes/StockTakesShow"),
      "StockTakesShow",
    ),
    path: eRoutes["/stock/stock-takes/:id"],
    routes: [
      {
        path: eRoutes["/stock/stock-takes/:id"],
        component: namedLazy(
          () => import("../Pages/Stock/StockTakes/StockTakesShowCounts"),
          "StockTakesShowCounts",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/stock-takes/:id/products"],
        component: namedLazy(
          () => import("../Pages/Stock/StockTakes/StockTakesShowProducts"),
          "StockTakesShowProducts",
        ),
        exact: true,
      },
      {
        path: eRoutes["/stock/stock-takes/:id/export"],
        component: namedLazy(
          () => import("../Pages/Stock/StockTakes/StockTakesExport"),
          "StockTakesExport",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/stock/tags"],
    component: namedLazy(
      () => import("../Pages/ProductMasterTags/ProductMasterTagsIndexPage"),
      "ProductMasterTagsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/tags/import"],
    component: namedLazy(
      () => import("../Pages/ProductMasterTags/ProductMasterTagsImportPage"),
      "ProductMasterTagsImportPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/tags/new"],
    component: namedLazy(
      () => import("../Pages/ProductMasterTags/ProductMasterTagsNewPage"),
      "ProductMasterTagsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/stock/tags/:id"],
    component: namedLazy(
      () => import("../Pages/ProductMasterTags/ProductMasterTagsEditPage"),
      "ProductMasterTagsEditPage",
    ),
  },
  {
    component: namedLazy(() => import("../Pages/Branches/ManageBranchPage"), "ManageBranchPage"),
    path: eRoutes["/manage-branch"],
    routes: [
      {
        path: eRoutes["/manage-branch"],
        component: namedLazy(
          () => import("../Pages/Branches/BranchesShowSummaryPage"),
          "BranchesShowSummaryPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/manage-branch/edit"],
        component: namedLazy(
          () => import("../Pages/Branches/BranchesShowPage"),
          "BranchesShowPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/manage-branch/internal-users"],
        component: namedLazy(
          () => import("../Pages/Branches/BranchesShowInternalUsersPage"),
          "BranchesShowInternalUsersPage",
        ),
        exact: true,
      },
      {
        path: eRoutes["/manage-branch/internal-users/new"],
        component: namedLazy(() => import("../Pages/SystemUsersNewPage"), "SystemUsersNewPage"),
        exact: true,
      },
      {
        path: eRoutes["/manage-branch/internal-users/:id"],
        component: namedLazy(() => import("../Pages/SystemUsersEditPage"), "SystemUsersEditPage"),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/product/:stock_code"],
    component: namedLazy(
      () => import("../Pages/BranchProductMasterShowPage"),
      "BranchProductMasterShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm"],
    component: namedLazy(() => import("../Pages/CRM/CRMDashboard"), "CRMDashboard"),
    exact: true,
  },
  {
    path: eRoutes["/crm/targets/sales-reps"],
    component: namedLazy(
      () => import("../Pages/CRM/Targets/SalesRepTargetsIndex"),
      "SalesRepTargetsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/targets/branches"],
    component: namedLazy(
      () => import("../Pages/CRM/Targets/BranchTargetsIndex"),
      "BranchTargetsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/customers"],
    component: namedLazy(
      () => import("../Pages/CRM/Customers/CRMCustomersIndexPage"),
      "CRMCustomersIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/customers/unlinked"],
    component: namedLazy(
      () => import("../Pages/Customers/CustomersUnlinkedIndexPage"),
      "CustomersUnlinkedIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/customers/sales-reps"],
    component: namedLazy(
      () => import("../Pages/CRM/Customers/CRMCustomersImportSalesRep"),
      "CRMCustomersImportSalesRep",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/leads"],
    component: namedLazy(() => import("../Pages/CRM/Leads/LeadsIndexPage"), "LeadsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/crm/leads/new"],
    component: namedLazy(() => import("../Pages/CRM/Leads/LeadsNewPage"), "LeadsNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/crm/leads/:id"],
    component: namedLazy(() => import("../Pages/CRM/Leads/LeadsEditPage"), "LeadsEditPage"),
    exact: true,
  },
  {
    path: eRoutes["/crm/projects"],
    component: namedLazy(
      () => import("../Pages/CRM/Projects/ProjectsIndexPage"),
      "ProjectsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/projects/new"],
    component: namedLazy(() => import("../Pages/CRM/Projects/ProjectsNewPage"), "ProjectsNewPage"),
    exact: true,
  },
  {
    component: namedLazy(() => import("../Pages/CRM/Projects/ProjectShowPage"), "ProjectShowPage"),
    path: eRoutes["/crm/projects/:id"],
    routes: [
      {
        path: eRoutes["/crm/projects/:id"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/CRM/Projects/ProjectsEditPage"),
          "ProjectsEditPage",
        ),
      },
      {
        path: eRoutes["/crm/projects/:id/log"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/CRM/Projects/ProjectsShowHistoryPage"),
          "ProjectsShowHistoryPage",
        ),
      },
    ],
  },
  {
    path: eRoutes["/crm/sales-reps"],
    component: namedLazy(
      () => import("../Pages/CRM/SalesReps/SalesRepsIndexPage"),
      "SalesRepsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/sales-reps/assignments"],
    component: namedLazy(
      () => import("../Pages/CRM/SalesReps/SalesRepAssignmentsPage"),
      "SalesRepAssignmentsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/sales-reps/new"],
    component: namedLazy(
      () => import("../Pages/CRM/SalesReps/SalesRepsNewPage"),
      "SalesRepsNewPage",
    ),
    exact: true,
  },
  {
    component: namedLazy(
      () => import("../Pages/CRM/SalesReps/SalesRepsShowPage"),
      "SalesRepsShowPage",
    ),
    path: eRoutes["/crm/sales-reps/:id"],
    routes: [
      {
        path: eRoutes["/crm/sales-reps/:id/edit"],
        component: namedLazy(() => import("../Pages/CRM/SalesReps/SalesRepEdit"), "SalesRepEdit"),
      },
      {
        path: eRoutes["/crm/sales-reps/:id/quotes"],
        component: namedLazy(
          () => import("../Pages/CRM/SalesReps/SalesRepsQuotesPage"),
          "SalesRepsQuotesPage",
        ),
      },
      {
        path: eRoutes["/crm/sales-reps/:id/orders"],
        component: namedLazy(
          () => import("../Pages/CRM/SalesReps/SalesRepsOrdersPage"),
          "SalesRepsOrdersPage",
        ),
      },
      {
        path: eRoutes["/crm/sales-reps/:id/customers"],
        component: namedLazy(
          () => import("../Pages/CRM/SalesReps/SalesRepCustomers"),
          "SalesRepCustomers",
        ),
      },
      {
        path: eRoutes["/crm/sales-reps/:id/tasks"],
        component: namedLazy(() => import("../Pages/CRM/SalesReps/SalesRepTasks"), "SalesRepTasks"),
      },
      {
        path: eRoutes["/crm/sales-reps/:id/events"],
        component: namedLazy(
          () => import("../Pages/CRM/SalesReps/SalesRepMicrosoftEventsPage"),
          "SalesRepMicrosoftEventsPage",
        ),
      },
      {
        path: eRoutes["/crm/sales-reps/:id/emails"],
        component: namedLazy(
          () => import("../Pages/CRM/SalesReps/SalesRepsCommunications"),
          "SalesRepsCommunications",
        ),
      },
      {
        path: eRoutes["/crm/sales-reps/:id/invoice-migrate"],
        component: namedLazy(
          () => import("../Pages/CRM/SalesReps/SalesRepInvoiceItemMigrator"),
          "SalesRepInvoiceItemMigrator",
        ),
      },
      {
        path: eRoutes["/crm/sales-reps/:id"],
        component: namedLazy(
          () => import("../Pages/CRM/SalesReps/SalesRepSummary"),
          "SalesRepSummary",
        ),
      },
    ],
  },
  // HR SECTION
  {
    path: eRoutes["/hr"],
    component: namedLazy(() => import("../Pages/HR/HrDashboard"), "HrDashboard"),
    exact: true,
  },

  // manage - index pages
  {
    path: eRoutes["/hr/holiday-requests"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Holiday/HolidayIndexPage"),
      "HolidayIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/hr/absence-requests"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Absence/AbsenceIndexPage"),
      "AbsenceIndexPage",
    ),
    exact: true,
  },
  // manage - new pages
  {
    path: eRoutes["/hr/holiday-requests/new"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Holiday/HolidayNewPage"),
      "HolidayNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/hr/absence-requests/new"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Absence/AbsenceNewPage"),
      "AbsenceNewPage",
    ),
    exact: true,
  },
  // manage - show/edit pages
  {
    path: eRoutes["/hr/manage-requests/:id"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Manage/HolidayRequestsShowPage"),
      "HolidayRequestsShowPage",
    ),
    routes: [
      {
        path: eRoutes["/hr/manage-requests/:id"],
        exact: true,
        component: namedLazy(
          () => import("../Pages/HR/HolidayRequests/Manage/HolidayRequestsShow"),
          "HolidayRequestsShow",
        ),
      },
      {
        path: eRoutes["/hr/manage-requests/:id/edit"],
        component: namedLazy(
          () => import("../Pages/HR/HolidayRequests/Manage/HolidayRequestsEdit"),
          "HolidayRequestsEdit",
        ),
      },
    ],
  },
  {
    path: eRoutes["/hr/probation-review"],
    component: namedLazy(
      () => import("../Pages/HR/ProbationReview/ProbationReviewIndex"),
      "ProbationReviewIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/hr/calendar-dates"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/CalendarDates/CalendarDatesIndex"),
      "CalendarDatesIndex",
    ),
    exact: true,
  },
  // user - index pages
  {
    path: eRoutes["/hr/requests/holiday"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Holiday/UserHolidayIndexPage"),
      "UserHolidayIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/hr/requests/absence"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Absence/UserAbsenceIndexPage"),
      "UserAbsenceIndexPage",
    ),
    exact: true,
  },
  // user - new pages
  {
    path: eRoutes["/hr/requests/holiday/new"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Holiday/UserHolidayNewPage"),
      "UserHolidayNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/hr/requests/absence/new"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/Absence/UserAbsenceNewPage"),
      "UserAbsenceNewPage",
    ),
    exact: true,
  },
  // user - show/edit pages
  {
    path: eRoutes["/hr/requests/:id"],
    component: namedLazy(
      () => import("../Pages/HR/HolidayRequests/User/UserHolidayRequestsShowPage"),
      "UserHolidayRequestsShowPage",
    ),
    routes: [
      {
        path: eRoutes["/hr/requests/:id"],
        component: namedLazy(
          () => import("../Pages/HR/HolidayRequests/User/UserHolidayRequestsShow"),
          "UserHolidayRequestsShow",
        ),
        exact: true,
      },
      {
        path: eRoutes["/hr/requests/:id/edit"],
        component: namedLazy(
          () => import("../Pages/HR/HolidayRequests/User/UserHolidayRequestsEdit"),
          "UserHolidayRequestsEdit",
        ),
      },
    ],
  },
  {
    path: eRoutes["/hr/teams"],
    component: namedLazy(() => import("../Pages/HR/Teams/HrTeamsIndex"), "HrTeamsIndex"),
    exact: true,
  },
  {
    path: eRoutes["/hr/teams/new"],
    component: namedLazy(() => import("../Pages/HR/Teams/HrTeamsNewPage"), "HrTeamsNewPage"),
  },
  {
    path: eRoutes["/hr/teams/:id"],
    component: namedLazy(() => import("../Pages/HR/Teams/HrTeamsShowPage"), "HrTeamsShowPage"),
    routes: [
      {
        path: eRoutes["/hr/teams/:id"],
        component: namedLazy(() => import("../Pages/HR/Teams/HrTeamsEditPage"), "HrTeamsEditPage"),
        exact: true,
      },
      {
        path: eRoutes["/hr/teams/:id/users"],
        component: namedLazy(() => import("../Pages/HR/Teams/HrTeamsUsers"), "HrTeamUsers"),
      },
    ],
  },

  // staff changes
  {
    path: eRoutes["/hr/staff-changes"],
    component: namedLazy(
      () => import("../Pages/HR/StaffChanges/StaffChangesIndexPage"),
      "StaffChangesIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/hr/staff-changes/new"],
    component: namedLazy(
      () => import("../Pages/HR/StaffChanges/StaffChangesNewPage"),
      "StaffChangesNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/hr/staff-changes/:id"],
    component: namedLazy(
      () => import("../Pages/HR/StaffChanges/StaffChangesEditPage"),
      "StaffChangesEditPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/sage-sync"],
    component: namedLazy(() => import("../Pages/SageSyncPage"), "SageSyncPage"),
  },
  {
    path: eRoutes["/reports"],
    component: namedLazy(() => import("../Pages/CRM/ReportsDashboard"), "ReportsDashboard"),
    exact: true,
  },
  {
    path: eRoutes["/reports/profit-loss"],
    component: namedLazy(() => import("../Pages/Reports/ProfitLossReport"), "ProfitLossReport"),
    exact: true,
  },
  {
    path: eRoutes["/reports/cash-sales"],
    component: namedLazy(() => import("../Pages/Reports/CashSalesReport"), "CashSalesReport"),
    exact: true,
  },
  {
    path: eRoutes["/reports/unexported-cash-sales"],
    component: () => <Redirect to={eRoutes["/reports/cash-sales"]} />,
    exact: true,
  },
  {
    path: eRoutes["/reports/project-registration"],
    component: namedLazy(
      () => import("../Pages/Reports/ProjectRegistrationReport"),
      "ProjectRegistrationReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/support-tickets/user"],
    component: namedLazy(
      () => import("../Pages/Reports/SupportTicketsReportByUser"),
      "SupportTicketsReportByUser",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/support-tickets/type"],
    component: namedLazy(
      () => import("../Pages/Reports/SupportTicketsReportByType"),
      "SupportTicketsReportByType",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/branch-stock-value"],
    component: namedLazy(
      () => import("../Pages/Reports/BranchStockValueReport"),
      "BranchStockValueReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/branch-stock-value"],
    component: namedLazy(
      () => import("../Pages/Reports/BranchStockValueReport"),
      "BranchStockValueReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/aged-stock-report"],
    component: namedLazy(() => import("../Pages/Reports/AgedStockReport"), "AgedStockReport"),
    exact: true,
  },
  {
    path: eRoutes["/reports/grn-stock"],
    component: namedLazy(() => import("../Pages/Reports/GRNStockReport"), "GRNStockReport"),
    exact: true,
  },
  {
    path: eRoutes["/reports/outstanding-or-products"],
    component: namedLazy(
      () => import("../Pages/Reports/OutstandingOrderRequestItemsReportPage"),
      "OutstandingOrderRequestItemsReportPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/reports/daily-sales"],
    component: namedLazy(
      () => import("../Pages/Reports/DailySales/DailyCentralSalesReport"),
      "DailyCentralSalesReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/daily-sales/ireland"],
    component: namedLazy(
      () => import("../Pages/Reports/DailySalesIreland/DailyCentralSalesReportIreland"),
      "DailyCentralSalesReportIreland",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/absent-users"],
    component: namedLazy(() => import("../Pages/Reports/AbsentUsersReport"), "AbsentUsersReport"),
    exact: true,
  },
  {
    path: eRoutes["/reports/average-margin"],
    component: namedLazy(
      () => import("../Pages/Reports/AverageMarginReportPage"),
      "AverageMarginReportPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/margin-by-supplier"],
    component: namedLazy(() => import("../Pages/Reports/MarginBySupplier"), "MarginBySupplier"),
    exact: true,
  },
  {
    path: eRoutes["/reports/realised-margin-by-supplier"],
    component: namedLazy(
      () => import("../Pages/Reports/RealisedMarginBySupplier"),
      "RealisedMarginBySupplier",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/margin-by-category"],
    component: namedLazy(() => import("../Pages/Reports/MarginByCategory"), "MarginByCategory"),
    exact: true,
  },
  {
    path: eRoutes["/reports/realised-margin-by-category"],
    component: namedLazy(
      () => import("../Pages/Reports/RealisedMarginByCategory"),
      "RealisedMarginByCategory",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/margin-by-product-group"],
    component: namedLazy(
      () => import("../Pages/Reports/MarginByProductGroup"),
      "MarginByProductGroup",
    ),
    exact: true,
  },
  {
    path: "/reports/sales-rep-margin-new",
    component: namedLazy(() => import("../Pages/Reports/MarginBySalesRep"), "MarginBySalesRep"),
    exact: true,
  },
  {
    path: "/reports/sales-rep-margin",
    component: namedLazy(
      () => import("../Pages/Reports/SalesRepsMarginReport"),
      "SalesRepsMarginReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/realised-margin-by-sales-rep"],
    component: namedLazy(
      () => import("../Pages/Reports/RealisedMarginBySalesRep"),
      "RealisedMarginBySalesRep",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/margin-threshold"],
    component: namedLazy(
      () => import("../Pages/Reports/MarginThresholdReportPage"),
      "MarginThresholdReportPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/margin-by-product-code"],
    component: namedLazy(
      () => import("../Pages/Reports/MarginByProductCode"),
      "MarginByProductCode",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/realised-margin-by-product-code"],
    component: namedLazy(
      () => import("../Pages/Reports/RealisedMarginByProductCode"),
      "RealisedMarginByProductCode",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/realised-margin-by-product-group"],
    component: namedLazy(
      () => import("../Pages/Reports/RealisedMarginByProductGroup"),
      "RealisedMarginByProductGroup",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/margin-by-customer"],
    component: namedLazy(() => import("../Pages/Reports/MarginByCustomer"), "MarginByCustomer"),
    exact: true,
  },
  {
    path: eRoutes["/reports/realised-margin-by-customer"],
    component: namedLazy(
      () => import("../Pages/Reports/RealisedMarginByCustomer"),
      "RealisedMarginByCustomer",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/branch-summary"],
    component: namedLazy(
      () => import("../Pages/Reports/BranchSummaryReport"),
      "BranchSummaryReport",
    ),
    exact: true,
  },

  {
    path: eRoutes["/reports/stock-value"],
    component: namedLazy(() => import("../Pages/Reports/StockValue"), "StockValue"),
    exact: true,
  },
  {
    path: eRoutes["/reports/delivery-method"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/DeliveryMethodReport/DeliveryMethodReport"),
      "DeliveryMethodReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/stock-cover"],
    component: namedLazy(() => import("../Pages/Reports/StockCoverReport"), "StockCoverReport"),
    exact: true,
  },
  {
    path: eRoutes["/reports/aged-debtors"],
    component: namedLazy(() => import("../Pages/Reports/AgedDebtorsReport"), "AgedDebtorsReport"),
    exact: true,
  },
  {
    path: eRoutes["/reports/aged-creditors"],
    component: namedLazy(
      () => import("../Pages/Reports/AgedCreditorsReport"),
      "AgedCreditorsReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/stock-cover-by-product"],
    component: namedLazy(
      () => import("../Pages/Reports/StockCoverByProductReport"),
      "StockCoverByProductReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/universal-orders"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/FulfillableOrderReport/FulfillableOrderReport"),
      "FulfillableOrderReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/sales-allocation-report"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/SalesOrderAllocations/SalesOrderAllocationsReport"),
      "SalesOrderAllocationsReport",
    ),
    exact: true,
  },
  // { path: eRoutes["/reports/variance-report-branch"], component: VarianceReportBranch, exact: true },
  {
    path: eRoutes["/reports/variance-report-branch"],
    component: namedLazy(
      () => import("../Pages/CRM/VarianceReportsInvoice/Reports/VarianceReportBranchInvoices"),
      "VarianceReportBranchInvoices",
    ),
    exact: true,
  },
  // { path: eRoutes["/reports/variance-report-customer"], component: VarianceReportCustomer, exact: true },
  {
    path: eRoutes["/reports/variance-report-customer"],
    component: namedLazy(
      () => import("../Pages/CRM/VarianceReportsInvoice/Reports/VarianceReportCustomerInvoices"),
      "VarianceReportCustomerInvoices",
    ),
    exact: true,
  },
  // { path: eRoutes["/reports/variance-report-user"], component: VarianceReportUser, exact: true },
  {
    path: eRoutes["/reports/variance-report-user"],
    component: namedLazy(
      () => import("../Pages/CRM/VarianceReportsInvoice/Reports/VarianceReportSalesRepInvoices"),
      "VarianceReportSalesRepInvoices",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/branch"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/BranchReport/BranchReport"),
      "BranchReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/branch-monthly"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/BranchMonthlyReport/BranchMonthlyReport"),
      "BranchMonthlyReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/sales"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/SalesReport/SalesReport"),
      "SalesReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/sales-monthly"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/SalesMonthlyReport/SalesMonthlyReport"),
      "SalesMonthlyReport",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/unallocated-order-items"],
    component: namedLazy(
      () => import("../Pages/CRM/VarianceReportsInvoice/Reports/UnallocatedOrderItemsPage"),
      "UnallocatedOrderItemsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/reports/builder"],
    component: namedLazy(
      () => import("../Pages/CRM/Reports/ReportBuilder/ReportBuilderPage"),
      "ReportBuilderPage",
    ),
    exact: true,
  },

  {
    path: eRoutes["/profile/microsoft"],
    component: namedLazy(() => import("./MicrosoftProfilePage"), "MicrosoftProfilePage"),
  },
  {
    path: eRoutes["/change-log"],
    component: namedLazy(() => import("../Pages/ChangeLog"), "ChangeLog"),
    exact: true,
  },
  {
    path: eRoutes["/system"],
    component: namedLazy(() => import("../Pages/SystemDashboardPage"), "SystemDashboardPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/reports-events"],
    component: namedLazy(() => import("../Pages/System/ReportEventsIndex"), "ReportEventsIndex"),
    exact: true,
  },
  {
    path: eRoutes["/system/roles"],
    component: namedLazy(() => import("../Pages/Roles/RolesIndexPage"), "RolesIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/roles/new"],
    component: namedLazy(() => import("../Pages/Roles/RolesNewPage"), "RolesNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/roles/:id"],
    component: namedLazy(() => import("../Pages/Roles/RoleShowPage"), "RoleShowPage") as any,
    routes: [
      {
        path: eRoutes["/system/roles/:id/users"],
        component: namedLazy(() => import("../Pages/Roles/RolesUsers"), "RolesUsers") as any,
        exact: true,
      },
      {
        path: eRoutes["/system/roles/:id"],
        component: namedLazy(() => import("../Pages/Roles/RolesEdit"), "RolesEdit") as any,
      },
    ],
  },
  {
    path: eRoutes["/system/customer-slideshow"],
    component: namedLazy(
      () => import("../Pages/CustomerSlideShows/CustomerSlideShowsIndex"),
      "CustomerSlideShowsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/customer-slideshow/new"],
    component: namedLazy(
      () => import("../Pages/CustomerSlideShows/CustomerSlideShowNewPage"),
      "CustomerSlideShowNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/customer-slideshow/reorder"],
    component: namedLazy(
      () => import("../Pages/CustomerSlideShows/CustomerSlidesReorderPage"),
      "CustomerSlidesReorderPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/customer-slideshow/:id"],
    component: namedLazy(
      () => import("../Pages/CustomerSlideShows/CustomerSlideShowEditPage"),
      "CustomerSlideShowEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/users"],
    component: namedLazy(() => import("../Pages/SystemUsersIndexPage"), "SystemUsersIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/videos"],
    component: namedLazy(() => import("../Pages/SystemVideosIndexPage"), "SystemVideosIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/crm/bulk-email"],
    component: namedLazy(
      () => import("../Pages/BulkEmails/BulkEmailsIndexPage"),
      "BulkEmailsIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/bulk-email/new"],
    component: namedLazy(
      () => import("../Pages/BulkEmails/BulkEmailsNewPage"),
      "BulkEmailsNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/bulk-email/:id"],
    component: namedLazy(
      () => import("../Pages/BulkEmails/BulkEmailsShowPage"),
      "BulkEmailsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/crm/bulk-email/:id/edit"],
    component: namedLazy(
      () => import("../Pages/BulkEmails/BulkEmailsEditPage"),
      "BulkEmailsEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/rma"],
    component: namedLazy(
      () => import("../Pages/BranchRma/BranchRmaIndexPage"),
      "BranchRmaIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/rma/new"],
    component: namedLazy(() => import("../Pages/BranchRma/BranchRmaNewPage"), "BranchRmaNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/rma/:id"],
    component: namedLazy(() => import("../Pages/BranchRma/BranchRmaShowPage"), "BranchRmaShow"),
    routes: [
      {
        path: eRoutes["/rma/:id"],
        component: namedLazy(
          () => import("../Pages/BranchRma/BranchRmaPreview"),
          "BranchRmaShowPreview",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/customer-support/rma"],
    component: namedLazy(() => import("../Pages/Rmas/RmasIndexPage"), "RmasIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/rma/new"],
    component: namedLazy(() => import("../Pages/Rmas/RmaNewPage"), "RmaNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/rma/:id"],
    component: namedLazy(() => import("../Pages/Rmas/RmaShowPage"), "RmaShowPage"),
    routes: [
      {
        path: eRoutes["/customer-support/rma/:id"],
        component: namedLazy(() => import("../Pages/Rmas/RmaEdit"), "RmaEdit"),
        exact: true,
      },
      {
        path: eRoutes["/customer-support/rma/:id/history"],
        component: namedLazy(() => import("../Pages/Rmas/RmaHistory"), "RmaHistory"),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/customer-support/return-requests"],
    component: namedLazy(
      () => import("../Pages/Returns/ReturnsRequestsIndex"),
      "ReturnsRequestsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/return-requests/:id"],
    component: namedLazy(
      () => import("../Pages/Returns/ReturnsRequestsShowPage"),
      "ReturnsRequestsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/trade-applications"],
    component: namedLazy(
      () => import("../Pages/TradeApplications/TradeApplicationsIndex"),
      "TradeApplicationsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/trade-applications/:id"],
    component: namedLazy(
      () => import("../Pages/TradeApplications/TradeApplicationsShowPage"),
      "TradeApplicationsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/pending-accounts"],
    component: namedLazy(
      () => import("../Pages/CRM/PendingAccounts/PendingAccountsIndex"),
      "PendingAccountsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/customer-support/pending-accounts/:id"],
    component: namedLazy(
      () => import("../Pages/CRM/PendingAccounts/PendingAccountsEditPage"),
      "PendingAccountsEditPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/users/:id"],
    component: namedLazy(() => import("../Pages/SystemUsersEditPage"), "SystemUsersEditPage"),
    exact: true,
  },
  {
    path: eRoutes["/stock/kits"],
    component: namedLazy(() => import("../Pages/Kits/KitsIndex"), "KitsIndex"),
    exact: true,
  },
  {
    path: eRoutes["/stock/kits/new"],
    component: namedLazy(() => import("../Pages/Kits/KitsNewPage"), "KitsNewPage"),
    exact: true,
  },
  {
    path: eRoutes["/stock/kits/:id"],
    component: namedLazy(() => import("../Pages/Kits/KitsEditPage"), "KitsEditPage"),
  },
  {
    path: eRoutes["/sage-purchase-orders"],
    component: namedLazy(
      () => import("../Pages/SagePurchaseOrders/SagePurchaseOrdersIndexPage"),
      "SagePurchaseOrdersIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/sage-purchase-orders/:id"],
    component: namedLazy(
      () => import("../Pages/SagePurchaseOrders/SagePurchaseOrdersShowPage"),
      "SagePurchaseOrdersShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/site-banners"],
    component: namedLazy(
      () => import("../Pages/SiteBanners/SiteBannerEditPage"),
      "SiteBannerEditPage",
    ),
  },
  {
    path: eRoutes["/system/link-tooltips"],
    component: namedLazy(
      () => import("../Pages/LinkTooltips/LinkTooltipsPage"),
      "LinkTooltipsPage",
    ),
  },
  {
    path: eRoutes["/system/preview-features"],
    component: namedLazy(() => import("../Pages/PreviewFeaturesPage"), "PreviewFeaturesPage"),
  },
  {
    path: eRoutes["/system/integrations/search-index"],
    component: namedLazy(() => import("../Pages/SearchIndexPage"), "SearchIndexPage"),
  },
  {
    path: eRoutes["/system/integrations/postcodes"],
    component: namedLazy(() => import("../Pages/PostcodeLookupsPage"), "PostcodeLookupsPage"),
  },
  { path: "/", component: namedLazy(() => import("./NotFoundPage"), "NotFoundPage") },
]

const superAdminRoutes: RouteConfig[] = [
  {
    path: eRoutes["/system/integrations/sage-auth"],
    component: namedLazy(() => import("../Pages/SageAuthPage"), "SageAuthPage"),
  },
  {
    path: eRoutes["/system/integrations/mrge"],
    component: namedLazy(() => import("../Pages/MrgeRequestsPage"), "MrgeRequestsPage"),
  },
  {
    path: eRoutes["/system/integrations/sage-http-requests"],
    component: namedLazy(() => import("../Pages/SageHttpRequestsPage"), "SageHttpRequestsPage"),
  },
  {
    path: eRoutes["/system/platform/branches"],
    exact: true,
    component: namedLazy(() => import("../Pages/Branches/BranchesIndexPage"), "BranchesIndexPage"),
  },
  {
    path: eRoutes["/system/platform/branches/new"],
    component: namedLazy(() => import("../Pages/Branches/BranchesNewPage"), "BranchesNewPage"),
  },
  {
    path: eRoutes["/system/monitoring/file-generations"],
    component: namedLazy(
      () => import("../Pages/System/FileGenerationsIndex"),
      "SystemFileGenerationsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/monitoring/alerts"],
    component: namedLazy(() => import("../Pages/Alerts/AlertsIndexPage"), "AlertsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/file-generations"],
    component: ({ location }: RouteComponentProps) => (
      <Redirect
        to={{
          pathname: eRoutes["/system/monitoring/file-generations"],
          search: location.search,
          hash: location.hash,
        }}
      />
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/alerts"],
    component: ({ location }: RouteComponentProps) => (
      <Redirect
        to={{
          pathname: eRoutes["/system/monitoring/alerts"],
          search: location.search,
          hash: location.hash,
        }}
      />
    ),
    exact: true,
  },
  {
    path: eRoutes["/system/monitoring/gr-pending-allocations"],
    component: namedLazy(
      () => import("../Pages/SuperAdminGoodsReceiptPendingAllocationsPage"),
      "SuperAdminGoodsReceiptPendingAllocationsPage",
    ),
  },
  {
    path: eRoutes["/system/monitoring/notifications"],
    component: namedLazy(
      () => import("../Pages/SuperAdminNotificationsPage"),
      "SuperAdminNotificationsPage",
    ),
  },
  {
    path: eRoutes["/system/sage-imports"],
    component: namedLazy(() => import("../Pages/SageImportsIndexPage"), "SageImportsIndexPage"),
    exact: true,
  },
  {
    path: eRoutes["/system/monitoring/permissions-audit"],
    component: namedLazy(
      () => import("../Pages/SuperAdminPermissionsAuditPage"),
      "SuperAdminPermissionsAuditPage",
    ),
  },
  {
    path: eRoutes["/system/monitoring/database-performance"],
    component: namedLazy(
      () => import("../Pages/SuperAdminDatabasePerformancePage"),
      "SuperAdminDatabasePerformancePage",
    ),
  },
  {
    path: eRoutes["/system/monitoring/logs"],
    component: namedLazy(() => import("../Pages/SuperAdminLogsPage"), "SuperAdminLogsPage"),
  },
  {
    path: eRoutes["/system/design-guide"],
    component: namedLazy(
      () => import("../Pages/SuperAdminDesignGuidePage"),
      "SuperAdminDesignGuidePage",
    ),
  },
  {
    path: eRoutes["/system"],
    component: namedLazy(() => import("../Pages/SystemDashboardPage"), "SystemDashboardPage"),
  },
  {
    path: eRoutes["/system/users"],
    component: namedLazy(() => import("../Pages/SystemUsersIndexPage"), "SystemUsersIndexPage"),
  },
  {
    path: eRoutes["/system/users/:id"],
    component: namedLazy(() => import("../Pages/SystemUsersEditPage"), "SystemUsersEditPage"),
  },
].map(route => ({ ...route, exact: true }))

const customerRoutes: RouteConfig[] = [
  {
    path: "/",
    component: namedLazy(() => import("../Pages/CustomerArea/CustomerHome"), "CustomerHome"),
    exact: true,
  },
  {
    path: eRoutes["/sign-in"],
    component: namedLazy(() => import("../Pages/Sessions/LogInPage2"), "LogInPage2"),
    exact: true,
  },
  {
    path: eRoutes["/deals-sign-in"],
    component: namedLazy(() => import("../Pages/Sessions/LogInPage2"), "LogInPage2"),
    exact: true,
  },
  {
    path: eRoutes["/sign-up"],
    component: namedLazy(
      () => import("../Pages/Sessions/CustomerRegisterPage"),
      "CustomerRegisterPage",
    ),
    exact: true,
  },
  {
    path: "/price-list",
    component: namedLazy(
      () => import("../Pages/AnonPriceListDownloadPage"),
      "AnonPriceListDownloadPage",
    ),
    exact: true,
  },
  {
    path: "/forgot-password",
    component: namedLazy(() => import("../Pages/ForgettenPasswordPage"), "ForgottenPasswordPage"),
    exact: true,
  },
  {
    path: "/reset-password",
    component: namedLazy(() => import("../Pages/UpdatePasswordPage"), "UpdatePasswordPage"),
    exact: true,
  },
  {
    path: "/accept-invite",
    component: namedLazy(() => import("../Pages/AcceptInvitePage"), "AcceptInvitePage"),
    exact: true,
  },
  {
    path: eRoutes["/statements"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerStatementsPage"),
      "CustomerStatementsPage",
    ),
    exact: true,
  },
  {
    path: "/my-dashboard",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerDashboard"),
      "CustomerDashboard",
    ),
    exact: true,
  },
  {
    path: eRoutes["/product/:stock_code"],
    component: namedLazy(
      () => import("../Pages/Customers/CustomerProductShowPage"),
      "CustomerProductShowPage",
    ),
    exact: true,
  },
  {
    path: "/clients/new",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerClientsNew"),
      "CustomerClientsNew",
    ),
    exact: true,
  },
  {
    path: "/clients/:id",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerClientsShow"),
      "CustomerClientsShow",
    ),
  },
  {
    path: eRoutes["/users"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerUsersIndex"),
      "CustomerUsersIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/users/new"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerUsersNew"),
      "CustomerUsersNew",
    ),
    exact: true,
  },
  {
    path: eRoutes["/users/:id"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerUsersEdit"),
      "CustomerUsersEdit",
    ),
  },
  {
    path: "/quotes/:id",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerQuoteShowPage"),
      "CustomerQuoteShowPage",
    ),
  },
  {
    path: "/jobs/new/:type?",
    component: namedLazy(
      () => import("../Pages/CustomerAreaNew/Jobs/CustomerJobNewPage"),
      "CustomerJobNewPage",
    ),
    exact: true,
  },
  {
    path: "/jobs/:id/edit",
    component: namedLazy(
      () => import("../Pages/CustomerAreaNew/Jobs/CustomerJobEditPage"),
      "CustomerJobEditPage",
    ),
  },
  {
    path: "/jobs/:id",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerJobsShow"),
      "CustomerJobsShow",
    ),
  },
  {
    path: "/checkout",
    component: namedLazy(
      () => import("../Pages/Customers/CustomerCheckoutPage"),
      "CustomerCheckoutPage",
    ),
  },
  {
    path: "/products",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerProductsIndexPage"),
      "CustomerProductsIndexPage",
    ),
  },
  {
    path: eRoutes["/deals"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerClearancePage"),
      "CustomerClearancePage",
    ),
    exact: true,
  },
  {
    path: "/brands",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerBrandsIndexPage"),
      "CustomerBrandsIndexPage",
    ),
    exact: true,
  },
  {
    path: "/quotes",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerQuotesIndex"),
      "CustomerQuotesIndexPage",
    ),
    exact: true,
  },
  {
    path: "/quotes/:id",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerQuoteShowPage"),
      "CustomerQuoteShowPage",
    ),
  },
  {
    path: "/orders",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerOrdersIndex"),
      "CustomerOrdersIndex",
    ),
    exact: true,
  },
  {
    path: "/orders/:id",
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerOrderShowPage"),
      "CustomerOrderShowPage",
    ),
  },
  {
    path: eRoutes["/branch/:id"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerBranchPage"),
      "CustomerBranchPage",
    ),
  },
  {
    path: eRoutes["/branch"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerBranchPage"),
      "CustomerBranchPage",
    ),
  },
  {
    path: eRoutes["/my-account"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerUserAccountCompanyPage"),
      "CustomerUserAccountCompanyPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/my-account/details"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerUserAccountFormPage"),
      "CustomerUserAccountFormPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/cart"],
    component: namedLazy(() => import("./CartPage"), "CartPage"),
    exact: true,
  },
  {
    path: eRoutes["/favourites/:groupId"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerProductFavouritesShowPage"),
      "CustomerProductFavouritesShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/favourites"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerProductFavouritesPage"),
      "CustomerProductFavouritesPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/return-requests"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerReturnsRequestsIndex"),
      "CustomerReturnsRequestsIndex",
    ),
    exact: true,
  },
  {
    path: eRoutes["/return-requests/:id"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerReturnsRequestsShowPage"),
      "CustomerReturnsRequestsShowPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/rma"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerRma/CustomerRmaIndexPage"),
      "CustomerRmaIndexPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/rma/new"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerRma/CustomerRmaNewPage"),
      "CustomerRmaNewPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/rma/:id"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerRma/CustomerRmaShowPage"),
      "CustomerRmaPreview",
    ),
    routes: [
      {
        path: eRoutes["/rma/:id"],
        component: namedLazy(
          () => import("../Pages/CustomerArea/CustomerRma/CustomerRmaPreview"),
          "CustomerRmaShowPreview",
        ),
        exact: true,
      },
    ],
  },
  {
    path: eRoutes["/support-and-returns"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/SupportAndReturnsPage"),
      "SupportAndReturnsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/support-and-returns/ireland"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/SupportAndReturnsIreland"),
      "SupportAndReturnsIreland",
    ),
    exact: true,
  },
  {
    path: eRoutes["/support-and-returns/uk"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/SupportAndReturnsUk"),
      "SupportAndReturnsUk",
    ),
    exact: true,
  },
  {
    path: eRoutes["/support-and-returns/customer-support/rma"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/SupportAndReturnsRmaForm"),
      "SupportAndReturnsRmaForm",
    ),
    exact: true,
  },
  {
    path: eRoutes["/application"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerTradeApplicationPage"),
      "CustomerTradeApplicationPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/terms-and-conditions"],
    component: namedLazy(
      () => import("../Pages/Sessions/TermsAndConditionsPage"),
      "TermsAndConditionsPage",
    ),
    exact: true,
  },
  {
    path: eRoutes["/privacy-policy"],
    component: namedLazy(() => import("../Pages/Sessions/PrivacyPolicyPage"), "PrivacyPolicyPage"),
    exact: true,
  },
  {
    path: eRoutes["/category/:idOrSlug"],
    component: namedLazy(
      () => import("../Pages/CustomerArea/CustomerProductsByCategoryPage"),
      "CustomerProductsByCategory",
    ),
  },

  { path: "/", component: namedLazy(() => import("./NotFoundPage"), "NotFoundPage") },
]

export function AppScene() {
  const globalState = useGlobalState()
  useLastSeenUser()
  useHrAreaOnly()
  const userRoutes = [...(globalState.currentUser.isSuperAdmin ? superAdminRoutes : []), ...routes]

  const location = useLocation()
  useEffect(() => {
    const route = userRoutes.find(route => matchPath(location.pathname, route))

    ;(window as any).params = route ? matchPath(location.pathname, route)?.params ?? {} : {} // @ts-ignore
    ;(window as any).id = route ? matchPath(location.pathname, route)?.params?.id ?? null : {}
  }, [location])

  const api = useApi()
  ;(window as any).api = api

  if (globalState.currentUser.userAccountType === "external" || !globalState.signedIn) {
    return (
      <div id="app-scene-portal-ref">
        <ThemeProvider.Extend theme={{ forms: { placeholderColor: themeColors.grey } }}>
          <ScrollTopRouteChange />
          <LoadingScreen isLoaded={!globalState.loading} />
          {!globalState.loading && (
            <React.Fragment>
              <CookiesBanner />
              <CartProvider>
                <ModalProvider>
                  <CustomerSiteWrapper>
                    <React.Suspense fallback={<LoadingFrame />}>
                      <ErrorBoundary>
                        <ManagedModalProvider />
                        {renderRoutes(customerRoutes)}
                      </ErrorBoundary>
                    </React.Suspense>
                  </CustomerSiteWrapper>
                </ModalProvider>
              </CartProvider>
            </React.Fragment>
          )}
        </ThemeProvider.Extend>
      </div>
    )
  }

  return (
    <div id="app-scene-portal-ref">
      <ScrollTopRouteChange />
      <LoadingScreen isLoaded={!globalState.loading} />
      {!globalState.loading && (
        <React.Fragment>
          <ModalProvider>
            {!globalState.signedIn && <ErrorBoundary>{renderRoutes(anonRoutes)}</ErrorBoundary>}
            {globalState.signedIn && (
              <SiteWrapper>
                <React.Suspense fallback={<LoadingFrame />}>
                  <ErrorBoundary>
                    <ManagedModalProvider />
                    <LinkTooltipEditorHost />
                    {renderRoutes(userRoutes)}
                  </ErrorBoundary>
                </React.Suspense>
              </SiteWrapper>
            )}
          </ModalProvider>
        </React.Fragment>
      )}
    </div>
  )
}

function ScrollTopRouteChange() {
  const history = useHistory()
  useScrollToTop([history.location.pathname])
  return null
}

export function useScrollToTop(deps: any[] = []) {
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [...deps])
}

export function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { currentUser } = useGlobalState()

  return (
    <ErrorBoundaryComp
      children={children}
      location={location}
      sessionData={{
        user: currentUser?.id,
        branch: currentUser?.branch?.id,
        customer: currentUser?.customer?.id,
        admin: currentUser.isAdmin,
        accounts: currentUser.isAccounts,
      }}
    />
  )
}

class ErrorBoundaryComp extends React.Component<{
  location: any
  sessionData?: Record<string, any>
}> {
  state: {
    path: string
    eventId: string
    error: any
    errored: boolean
    expiredSession: boolean
  } = {
    errored: false,
    error: undefined,
    path: "",
    eventId: "",
    expiredSession: false,
  }

  componentDidUpdate() {
    const { errored, path } = this.state
    if (errored && path !== this.props.location.pathname) {
      this.setState({ errored: false, error: undefined, path: "", expiredSession: false })
    }
  }

  componentDidCatch(error: Error | null, errorInfo: Record<string, any>) {
    console.error(error, errorInfo)

    // Almost always a deploy: this tab still holds hashed chunk names that the
    // new build no longer serves. Matching on Chrome's wording alone used to
    // skip this entirely in Safari, and a plain reload could be answered from
    // cache with the same stale index.html — see utils/chunkLoadError.
    if (isChunkLoadError(error)) {
      reloadForNewBuild()
    }

    // An expired session is not a crash. Drop the dead token, go back to the
    // login screen, and report nothing — see utils/expiredSession.
    const expiredSession = isExpiredSessionError(error)
    if (expiredSession) {
      redirectForExpiredSession()
    }

    this.setState({
      errored: true,
      error,
      errorInfo,
      expiredSession,
      path: this.props.location.pathname,
    })
  }

  render() {
    if (this.state.expiredSession) {
      return <ExpiredSessionPage />
    }

    if (this.state.errored) {
      return (
        <div>
          <ErrorPage
            errorDetails={{
              errorMessage: this.state.error?.message,
              stack: this.state.error?.stack,
              path: this.state.path,
              session: this.props.sessionData,
            }}
            // error={this.state.error ? this.state.error : undefined}
          />
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * Shown for the moment between spotting a dead session and the browser leaving
 * for `/`. It is also the fallback if the redirect was refused because we have
 * just done one, so it has to give the user a way out by hand rather than
 * assuming the navigation is on its way.
 */
function ExpiredSessionPage() {
  return (
    <div
      css={css`
        padding: 200px 40px;
        max-width: 600px;
      `}
    >
      <TextGroupLarge
        heading="Your session has expired"
        body="Taking you back to the sign in page. If nothing happens, please refresh."
      />
    </div>
  )
}

const ignoreErrors = [
  "is not a valid JavaScript MIME type",
  "dynamically imported module",
  "Network error",
  "load failed",
  "importing a module script failed",
  "reading 'querySelectorAll'",
  "use fetchMe when not logged in",
  "Failed to execute 'insertBefore' on 'Node'",
  "too much recursion",
].map(x => x.toLowerCase())
function ErrorPage({ errorDetails }: { errorDetails: any }) {
  const skeem = window.skeem
  const isErrorToIgnore =
    (typeof errorDetails?.errorMessage === "string" &&
      ignoreErrors.some(error => errorDetails.errorMessage.toLowerCase().includes(error))) ||
    // The boundary already handles these, but the key survives in the stack, so
    // a session error arriving here by any other route still raises no alert.
    isExpiredSessionError({ message: errorDetails?.errorMessage, stack: errorDetails?.stack })

  useAsyncEffect(async () => {
    if (isErrorToIgnore) {
      return
    }
    let user = null
    try {
      user = await skeem.fetchMe([
        "customer",
        { as: "branchName", value: { path: ["branch", "name"] } },
      ])
    } catch (e) {
      console.log("Error fetching user for error report", e)
    }

    console.log(errorDetails, "errorDetails")
    await skeem.mutate({
      alerts: {
        create: {
          type: "500-page",
          body: errorDetails.errorMessage ?? "An error occurred",
          details: { error: errorDetails, user },
          notifications: ["support@cd2solutions.co.uk"],
        },
      },
    })
  }, [])

  return (
    <div
      css={css`
        padding: 200px 40px;
        max-width: 600px;
      `}
    >
      {isErrorToIgnore ? (
        <TextGroupLarge
          heading="Update available — please refresh"
          body="A new version of the app was just deployed. Please refresh the page to continue. There's no need to report this error."
        />
      ) : (
        <TextGroupLarge
          heading="Sorry, you've hit an error"
          body="Something unexpected has occurred, we've notified the development team who will take a look. If this issue persists, please do get in touch."
        />
      )}
      <pre
        css={css`
          font-size: 10px;
          color: #999;
          word-break: break-all;
          max-width: 400px;
          overflow: auto;
        `}
      >
        {JSON.stringify(errorDetails, null, 2)}
      </pre>
    </div>
  )
}
